import { motion } from "framer-motion";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: [0.33, 1, 0.68, 1] },
};

export const KravMagaSection = () => {
  return (
    <section
      id="krav-maga"
      data-testid="krav-maga-section"
      className="relative px-6 md:px-16 lg:px-24 py-12 md:py-16"
    >
      <div className="section-divider mb-10 md:mb-14" />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        {/* Left: headline + archival image */}
        <motion.div {...fadeUp} className="md:col-span-4">
          <div className="brass-line mb-4" />
          <h2
            data-testid="krav-maga-headline"
            className="font-['Oswald'] text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-[0.95] text-ink mb-6"
          >
            Not a Sport.<br />
            Not a Ritual.<br />
            <span className="text-sig">A System.</span>
          </h2>

          {/* Archival history photo */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
          >
            <img
              src="/images/history.jpg"
              alt="Early Krav Maga training — IDF archival"
              className="w-full h-auto object-cover"
            />
            <p className="mt-2 text-[9px] tracking-[0.2em] uppercase text-mute font-['IBM_Plex_Sans']">
              IDF knife defense training, circa 1950s
            </p>
          </motion.div>
        </motion.div>

        {/* Right: editorial text */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="md:col-span-8 md:pl-4"
        >
          <span className="text-[9px] tracking-[0.3em] uppercase text-sig font-['IBM_Plex_Sans'] block mb-4">
            Est. 1948 — Israeli Defense Forces
          </span>

          <div className="space-y-4 text-sm leading-[1.75] text-mute font-['IBM_Plex_Sans'] font-light max-w-2xl">
            <p>
              Krav Maga was born on the streets of Bratislava in the late 1930s, where Imi Lichtenfeld — champion wrestler, skilled boxer — discovered that sport doesn't work when people actually want to hurt you. He organized Jewish men to defend their neighborhood against fascist gangs and had to throw out the rulebook entirely.
            </p>
            <p>
              When Israel declared independence in 1948, Imi was there. The newly formed IDF needed a combat system that could be taught in weeks, worked under extreme stress, and required no years of dedicated practice. Imi became their chief hand-to-hand combat instructor for 20 years.
            </p>
            <p className="text-base md:text-lg leading-[1.8] text-ink font-['IBM_Plex_Sans'] font-light border-l-2 border-sig pl-6">
              The Krav Maga philosophy is simple — situational awareness first, de-escalation when possible, aggression only when unavoidable. The goal isn't to win a fight. It's to not be in one — and if you have no choice, to end it fast and get out.
            </p>
          </div>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-8"
          >
            <p className="text-sm leading-[1.75] text-mute font-['IBM_Plex_Sans'] font-light mb-4">
              Imi Lichtenfeld died in 1998 at 87 — still training into his 80s. His parting instruction to his students:
            </p>
            <blockquote className="border-l-2 border-sig pl-6">
              <p
                data-testid="krav-maga-quote"
                className="font-['Oswald'] text-xl md:text-2xl font-bold uppercase tracking-tight text-sig"
              >
                "So that one may walk in peace."
              </p>
            </blockquote>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
